import { Grid, CardHeader, CardContent, Box, TextField, Avatar, Button, CircularProgress, Select, MenuItem, FormHelperText } from "@mui/material";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import { IMAGE_BASE } from "../../utils/constants";
import CardLayout from "../../layouts/CardLayout";
import { toast } from "react-toastify";
import errorHandler from "../../utils/errorHandler";
import { TestimonialsServices } from "../../services/AllServices";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";

function AddEditTestimonials() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [data, setData] = useState();

  const fileInputRef = useRef(null);
  const ALLOWED_CHARACTERS_REGEX = /^[A-Za-z0-9\s]*$/;

  const handleKeyPress = (e) => {
    if (!ALLOWED_CHARACTERS_REGEX.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageLoading(true);
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setImageLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await TestimonialsServices.getById({ id });
        const resData = response?.data?.data;
        setImagePreview(IMAGE_BASE + resData?.image);
        setData(resData);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      clientName: Yup.string().required("Client Name is required"),
      description: Yup.string().required("Description is required"),
      ticketBoughtTo: Yup.string().required("Event name is required"),
      status: Yup.string().notRequired(),
    }),
    initialValues: {
      clientName: data?.clientName || "",
      description: data?.description || "",
      ticketBoughtTo: data?.ticketBoughtTo || "",
      status: data?.status || "",
      filePath: "testimonial",
      image: data?.image || null,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const formData = new FormData();
        if (id) formData.append("id", id);
        formData.append("clientName", values.clientName.trim());
        formData.append("description", values.description.trim());
        formData.append("ticketBoughtTo", values.ticketBoughtTo.trim());
        if (values.status) formData.append("status", values.status.trim());
        formData.append("filePath", "testimonial");
        if (selectedImage) {
          formData.append("image", selectedImage);
        }

        const response = id
          ? await TestimonialsServices.edit(formData)
          : await TestimonialsServices.add(formData);

        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Testimonial`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <input
                    accept="image/*"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                  <Box
                    mt={2}
                    onClick={() => fileInputRef.current.click()}
                    sx={{
                      width: 120,
                      height: 120,
                      mx: "auto",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #ccc",
                      borderRadius: "50%",
                    }}
                  >
                    {imageLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Avatar
                        src={imagePreview || "/path/to/default-avatar.jpg"}
                        alt="Preview"
                        sx={{ width: 100, height: 100 }}
                      />
                    )}
                  </Box>
                  {formik.touched.image && formik.errors.image && (
                    <Box mt={1} color="error.main" textAlign="center">
                      {formik.errors.image}
                    </Box>
                  )}
                  <Box mt={2} display="flex" justifyContent="center">
                    <Button
                      size="large"
                      type="button"
                      variant="contained"
                      onClick={() => fileInputRef.current.click()}
                    >
                      {id ? "Edit" : "Upload"} Image
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Client Name"
                    required
                    name="clientName"
                    value={formik.values.clientName}
                    onChange={formik.handleChange}
                    onKeyPress={handleKeyPress}
                    error={Boolean(
                      formik.touched.clientName && formik.errors.clientName
                    )}
                    helperText={
                      formik.touched.clientName && formik.errors.clientName
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Description"
                    required
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    error={Boolean(
                      formik.touched.description && formik.errors.description
                    )}
                    helperText={
                      formik.touched.description && formik.errors.description
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Event name"
                    required
                    name="ticketBoughtTo"
                    value={formik.values.ticketBoughtTo}
                    onChange={formik.handleChange}
                    error={Boolean(
                      formik.touched.ticketBoughtTo &&
                      formik.errors.ticketBoughtTo
                    )}
                    helperText={
                      formik.touched.ticketBoughtTo &&
                      formik.errors.ticketBoughtTo
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Select
                    labelId="status-label"
                    id="status"
                    value={formik.values.status}
                    onChange={(e) =>
                      formik.setFieldValue("status", e.target.value)
                    }
                    fullWidth
                    error={Boolean(
                      formik.touched.status && formik.errors.status
                    )}
                    displayEmpty
                    defaultValue=""
                  >
                    <MenuItem value="">Select Status</MenuItem>
                    <MenuItem value={"active"}>Active</MenuItem>
                    <MenuItem value={"inactive"}>Inactive</MenuItem>
                  </Select>
                  {formik.touched.status && formik.errors.status && (
                    <FormHelperText error>
                      {formik.errors.status}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"                     
                    >
                      {id ? "Update" : "Add"} Testimonial
                    </LoadingButton>
                  </Box>
                </Grid>

              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditTestimonials;